/**
 * PII / PHI scrubber for the in-house error reporter.
 *
 * Runs on the browser side BEFORE the payload leaves the user's machine,
 * with the goal that nothing identifiable (email, phone, SSN, JWT,
 * API key, credit-card-like number) reaches our edge function. The
 * server-side scrubber in `errors.fn_scrub_text` (migration
 * 20260523000200_errors_ingest_rpc.sql) catches anything that slips
 * past as defense in depth, but it's expensive to redact at the DB
 * layer on every event so the bulk of the work happens here.
 *
 * The allowlist model for `context` / `tags` is deliberate: anything
 * we haven't explicitly opted in to is dropped to `[redacted]`. That
 * means adding a new context key is a code change, not an accident.
 *
 * `redacted_paths` is populated alongside the scrubbed payload so the
 * admin UI can render a "[redacted]" pill in line with the rest of
 * the rendered object instead of silently disappearing the field.
 */

/**
 * Context keys we explicitly allow through unredacted. Everything else
 * gets the `[redacted]` placeholder. Adding to this list is a deliberate
 * code change.
 */
export const CONTEXT_ALLOWLIST: ReadonlySet<string> = new Set([
  'app',
  'release',
  'environment',
  'route',
  'feature',
  'feature_flag',
  'feature_flags',
  'module',
  'module_id',
  'active_modules',
  'plan',
  'plan_key',
  'billing_status',
  'org_id',
  'store_id',
  'user_id',
  'request_id',
  'session_id',
  'page',
  'page_title',
  'view',
  'tab',
  'role',
  'membership_role',
  'is_platform_admin',
  'locale',
  'timezone',
  'experiment',
  'experiment_variant',
  'mutation',
  'query',
  'query_key',
  'http_status',
  'duration_ms',
  'attempt',
  'retry_count',
  'category',
  'subcategory',
  'kind',
  'subject_kind',
  'severity',
  'level',
  'reason',
]);

const REDACTED = '[redacted]';

const PATTERNS: Array<{ name: string; regex: RegExp; replacement: string }> = [
  // JWTs (header.payload.signature, base64url-ish)
  { name: 'jwt', regex: /(bearer\s+|basic\s+)?eyJ[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+){2}/gi, replacement: '<jwt>' },
  // Supabase / Stripe / Resend style live/test keys
  { name: 'api_key', regex: /\b(?:sk|pk|rk|whsec|re)_[A-Za-z0-9_-]{16,}\b/g, replacement: '<key>' },
  // Long random tokens that don't match the patterns above (anon keys, etc.)
  { name: 'long_token', regex: /\b[A-Za-z0-9_-]{40,}\b/g, replacement: '<token>' },
  // Emails
  { name: 'email', regex: /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, replacement: '<email>' },
  // US-style SSN
  { name: 'ssn', regex: /\b\d{3}-\d{2}-\d{4}\b/g, replacement: '<ssn>' },
  // 13-19 digit runs (credit cards)
  { name: 'card', regex: /\b\d{13,19}\b/g, replacement: '<card>' },
  // E.164-ish phones (10-15 digits with optional separators / parens)
  { name: 'phone', regex: /\+?\d[\d\s\-().]{8,}\d/g, replacement: '<phone>' },
];

/**
 * Scrub a free-form string. Returns the scrubbed value plus the names
 * of every pattern that matched (for redacted_paths surfacing).
 */
export function scrubText(input: string | null | undefined, maxLen = 8000):
  { value: string | null; matched: string[] } {
  if (input == null) return { value: null, matched: [] };
  let v = input;
  const matched = new Set<string>();
  for (const p of PATTERNS) {
    if (p.regex.test(v)) {
      matched.add(p.name);
      v = v.replace(p.regex, p.replacement);
    }
  }
  if (v.length > maxLen) v = v.slice(0, maxLen);
  return { value: v, matched: [...matched] };
}

/**
 * Deep-scrub a value:
 *   * strings -> scrubText
 *   * arrays  -> recurse per element
 *   * objects -> recurse per key; keys NOT on `allowlist` are replaced
 *               with the `[redacted]` placeholder.
 *
 * Top-level call should pass `path = '$'`. `redactedPaths` accumulates
 * jsonpath strings for each redaction so the admin UI can render an
 * inline "redacted" marker.
 */
export function scrubObject(
  value: unknown,
  allowlist: ReadonlySet<string> = CONTEXT_ALLOWLIST,
  path = '$',
  redactedPaths: string[] = [],
  depth = 0,
): { value: unknown; redactedPaths: string[] } {
  // Cheap guards against runaway structures.
  if (depth > 8) {
    redactedPaths.push(path + ' (depth)');
    return { value: '[redacted depth]', redactedPaths };
  }

  if (value == null) return { value, redactedPaths };

  if (typeof value === 'string') {
    const { value: v, matched } = scrubText(value);
    if (matched.length > 0) redactedPaths.push(path + ` (${matched.join(',')})`);
    return { value: v, redactedPaths };
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return { value, redactedPaths };
  }

  if (Array.isArray(value)) {
    const out = value.slice(0, 50).map((item, i) =>
      scrubObject(item, allowlist, `${path}[${i}]`, redactedPaths, depth + 1).value,
    );
    return { value: out, redactedPaths };
  }

  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    let count = 0;
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (count >= 50) {
        redactedPaths.push(path + ' (truncated)');
        break;
      }
      count++;
      const childPath = `${path}.${k}`;
      if (!allowlist.has(k)) {
        out[k] = REDACTED;
        redactedPaths.push(childPath + ' (not allowlisted)');
        continue;
      }
      out[k] = scrubObject(v, allowlist, childPath, redactedPaths, depth + 1).value;
    }
    return { value: out, redactedPaths };
  }

  return { value: String(value), redactedPaths };
}

/**
 * Tags get a more permissive treatment than `context`: any string-keyed
 * scalar (string / number / boolean) is allowed through after a text
 * scrub. Nested objects in tags are flattened to `[object]`.
 */
export function scrubTags(
  tags: Record<string, unknown> | null | undefined,
  redactedPaths: string[] = [],
): { value: Record<string, unknown>; redactedPaths: string[] } {
  const out: Record<string, unknown> = {};
  if (!tags) return { value: out, redactedPaths };
  let count = 0;
  for (const [k, v] of Object.entries(tags)) {
    if (count >= 32) break;
    count++;
    if (v == null) continue;
    if (typeof v === 'string') {
      const { value, matched } = scrubText(v, 256);
      if (matched.length > 0) redactedPaths.push(`$.tags.${k} (${matched.join(',')})`);
      out[k] = value;
    } else if (typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    } else {
      out[k] = '[object]';
    }
  }
  return { value: out, redactedPaths };
}
